<template>
  <div class="app-user-role-avatar" :class="{ 'app-user-role-avatar-inline': inline }">
    <b-dropdown
      v-if="editable"
      variant="link"
      no-caret
      boundary="viewport"
      toggle-class="app-user-role-avatar-toggle"
      menu-class="app-user-role-avatar-menu"
      :popper-opts="{ positionFixed: true }"
      :disabled="loading"
      @show="onDropdownShow"
    >
      <template #button-content>
        <span :class="avatarClasses" :title="avatarTitle">{{ avatarLabel }}</span>
      </template>
      <b-dropdown-header>{{ roleLabel }}</b-dropdown-header>
      <b-dropdown-form @submit.prevent class="app-user-role-avatar-search">
        <b-form-input
          ref="searchInput"
          v-model="search"
          size="sm"
          :placeholder="$t('search')"
          autocomplete="off"
        />
      </b-dropdown-form>
      <b-dropdown-item-button v-if="user" @click="onSelect(null)">
        <i>{{ $t("user-role-avatar-clear") }}</i>
      </b-dropdown-item-button>
      <b-dropdown-item-button
        v-for="candidate in filteredUsers"
        :key="candidate.id"
        :active="!!user && candidate.id === user.id"
        @click="onSelect(candidate.id)"
      >
        {{ fullName(candidate) }}
      </b-dropdown-item-button>
      <b-dropdown-text v-if="filteredUsers.length === 0" class="text-muted">
        {{ $t("no-results") }}
      </b-dropdown-text>
    </b-dropdown>
    <span v-else :class="avatarClasses" :title="avatarTitle">{{ avatarLabel }}</span>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Ref } from "vue-property-decorator";
import { BaseComponent } from "../base-component/base-component";
import { CatchError } from "@/app/shared/services/helper/catch-helper";
import { getUserNameInitials } from "@/app/shared/services/helper/string-helper";
import { AnalysisRolesHelper } from "@/app/shared/services/helper/analysis-roles-helper";
import { AvatarUserSchema, SimpleUserRoleSchema } from "@/app/shared/services/volateq-api/api-schemas/user-schemas";

interface IAppFormInput {
  focus(): void;
}

export type UserRole = "f2c" | "ops";

@Component({
  name: "app-user-role-avatar",
})
export default class AppUserRoleAvatar extends BaseComponent {
  @Prop({ default: null }) user!: AvatarUserSchema | null;
  @Prop({ required: true }) role!: UserRole;
  @Prop({ default: false }) editable!: boolean;
  @Prop({ default: () => [] }) users!: SimpleUserRoleSchema[];
  @Prop({ default: false }) loading!: boolean;
  @Prop({ default: false }) inline!: boolean;

  @Ref() searchInput?: IAppFormInput;

  search = "";

  get roleLabel(): string {
    return this.$t(this.role === "f2c" ? "user-role-f2c" : "user-role-ops").toString();
  }

  get avatarLabel(): string {
    return this.user ? getUserNameInitials(this.user) : "";
  }

  get avatarTitle(): string {
    if (this.user) {
      return `${this.roleLabel}: ${this.fullName(this.user)}`;
    }
    return `${this.roleLabel}: ${this.$t("user-role-avatar-unassigned")}`;
  }

  get avatarClasses(): Record<string, boolean> {
    return {
      "app-user-role-avatar-circle": true,
      [`app-user-role-avatar-${this.role}`]: true,
      "app-user-role-avatar-unassigned": !this.user,
    };
  }

  get roleCandidates(): SimpleUserRoleSchema[] {
    return AnalysisRolesHelper.candidatesForRole(this.users, this.role);
  }

  get filteredUsers(): SimpleUserRoleSchema[] {
    const query = this.search.trim().toLowerCase();
    if (!query) {
      return this.roleCandidates;
    }
    return this.roleCandidates.filter(user => this.fullName(user).toLowerCase().includes(query));
  }

  fullName(user: AvatarUserSchema): string {
    return `${user.first_name} ${user.last_name}`.trim();
  }

  @CatchError()
  onDropdownShow() {
    this.search = "";
    this.$nextTick(() => this.searchInput?.focus());
  }

  @CatchError()
  onSelect(userId: string | null) {
    this.$emit("select", userId);
  }
}
</script>

<style lang="scss">
@import "@/scss/_colors.scss";

.app-user-role-avatar {
  display: inline-block;

  &-inline {
    vertical-align: middle;
  }

  .dropdown.b-dropdown {
    .app-user-role-avatar-toggle {
      padding: 0;
      border: none;
      background: none;
      line-height: 0;
      box-shadow: none !important;

      &:hover,
      &:focus {
        box-shadow: none !important;
      }
    }
  }

  &-circle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    font-size: 0.7rem;
    font-weight: 700;
    line-height: 1;
    text-transform: uppercase;
    border: 2px solid transparent;
    box-sizing: border-box;
    user-select: none;
  }

  // Light blue background for F2C (Face to the customer)
  &-f2c {
    background-color: #cfe2fb;
    color: #054a9e;
  }

  // Full Volateq blue for Ops (Operations)
  &-ops {
    background-color: #066be2;
    color: #ffffff;
  }

  // Red border whenever no user has been assigned to the role yet
  &-unassigned {
    border-color: #dc3545;
  }
}

.app-user-role-avatar-menu.dropdown-menu {
  max-height: 320px;
  overflow-y: auto;
  background-color: var(--default-bg-color);
  border-color: var(--default-border-color);
  color: var(--default-text-color);

  .dropdown-header {
    color: var(--muted-text-color);
  }

  .dropdown-item {
    color: var(--default-text-color);

    &:hover,
    &:focus {
      background-color: var(--default-bg-hover-color);
      color: var(--default-text-hover-color);
    }

    &.active,
    &:active {
      background-color: var(--primary-bg-color);
      color: var(--primary-text-color);
    }
  }

  .b-dropdown-text {
    color: var(--muted-text-color);
  }

  .app-user-role-avatar-search {
    padding: 0.25rem 0.75rem 0.5rem;

    .form-control {
      background-color: var(--input-bg-color);
      border-color: var(--default-border-color);
      color: var(--default-text-color);

      &::placeholder {
        color: var(--muted-text-color);
      }
    }
  }
}
</style>
